import { Button, Heading, ProgressCircle } from '@react-spectrum/s2';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext';
import { AnimateIn } from '@/components/AnimateIn';
import { EditableTagList } from '@/components/EditableTagList';
import { InstagramIcon } from '@/components/icons/InstagramIcon';
import { useEditableTagList } from '@/hooks/useEditableTagList';
import { logger } from '@/logger';
import { trackEvent } from '@/utils/fullstory';

import type {
	BusinessProfile,
	TestVoiceRequestBody,
} from '../../../../../bridge/types/business-profile.ts';
import { EditableCard } from '../EditableCard';
import { cardTitleStyles, PILL_TAG } from '../styles';

const autoTriggeredUserIds = new Set<string>();

type VoiceTabProps = {
	profile: BusinessProfile | undefined;
	onArraySave: (path: string, value: string[]) => void;
};

interface TestVoiceResult {
	platform: string;
	caption: string;
}

export const VoiceTab = memo(({ profile, onArraySave }: VoiceTabProps) => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();

	const narrative = profile?.narrative;

	const rulesTags = useEditableTagList({
		serverValue: narrative?.voice_writing_rules?.value ?? [],
		onSave: (values) => onArraySave('narrative.voice_writing_rules', values),
	});

	const stylesTags = useEditableTagList({
		serverValue: narrative?.writing_style?.value ?? [],
		onSave: (values) => onArraySave('narrative.writing_style', values),
	});

	const emojisTags = useEditableTagList({
		serverValue: narrative?.emojis?.value ?? [],
		onSave: (values) => onArraySave('narrative.emojis', values),
	});

	const pillarsTags = useEditableTagList({
		serverValue: narrative?.content_pillars?.value ?? [],
		onSave: (values) => onArraySave('narrative.content_pillars', values),
	});

	const [output, setOutput] = useState<string>('');
	const [isGenerating, setIsGenerating] = useState(false);

	const handleTestVoice = useCallback(async () => {
		if (!userId || !profile || isGenerating) return;
		trackEvent('Profile Voice Tested');
		setIsGenerating(true);
		try {
			const payload: TestVoiceRequestBody = {
				narrative: {
					voice_writing_rules: {
						value: rulesTags.displayTags,
						inferred: false,
					},
					writing_style: {
						value: stylesTags.displayTags,
						inferred: false,
					},
					emojis: {
						value: emojisTags.displayTags,
						inferred: false,
					},
				},
			};
			const result = await bridgewayApi.post<TestVoiceResult>(
				`/api/user/${userId}/business-profile/test-voice`,
				payload,
			);
			setOutput(result.caption);
		} catch (err) {
			logger.error('Test voice failed', err);
			setOutput('Failed to generate sample. Please try again.');
		} finally {
			setIsGenerating(false);
		}
	}, [
		userId,
		profile,
		isGenerating,
		bridgewayApi,
		rulesTags.displayTags,
		stylesTags.displayTags,
		emojisTags.displayTags,
	]);

	const hasAutoTriggered = useRef(false);
	useEffect(() => {
		if (hasAutoTriggered.current) return;
		if (!profile || !userId || isGenerating || output) return;
		hasAutoTriggered.current = true;
		if (autoTriggeredUserIds.has(userId)) return;
		autoTriggeredUserIds.add(userId);
		void handleTestVoice();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [profile, userId]);

	return (
		<>
			<div className="grid grid-cols-3 gap-6">
				{/* Column 1: Writing Rules + Content Pillars */}
				<AnimateIn delay={0} className="h-full">
					<div className="flex h-full flex-col gap-6">
						<EditableCard
							title="Writing Rules"
							isEditing={rulesTags.isEditing}
							onEdit={rulesTags.onEdit}
							onSave={rulesTags.onSave}
							onCancel={rulesTags.onCancel}
						>
							{rulesTags.isEditing ? (
								<EditableTagList
									tags={rulesTags}
									inputComponent="TextArea"
									placeholder="Add rule"
								/>
							) : (
								<div
									className="flex flex-col text-sm text-[var(--text-primary)]"
								>
									{rulesTags.displayTags.map((item, i) => (
										<div
											key={i}
											className={`pb-3 ${i > 0 ? 'pt-3' : ''}
												${i < rulesTags.displayTags.length - 1 ? 'border-b border-gray-200' : ''}`}
										>
											{item}
										</div>
									))}
									{rulesTags.displayTags.length === 0 && (
										<div className="text-[var(--text-muted)]">
											No rules defined yet
										</div>
									)}
								</div>
							)}
						</EditableCard>

						<EditableCard
							title="Content Pillars"
							isEditing={pillarsTags.isEditing}
							onEdit={pillarsTags.onEdit}
							onSave={pillarsTags.onSave}
							onCancel={pillarsTags.onCancel}
							className="flex-1"
						>
							{pillarsTags.isEditing ? (
								<EditableTagList tags={pillarsTags} placeholder="Add pillar" />
							) : (
								<div className="flex flex-wrap gap-2">
									{pillarsTags.displayTags.map((pillar, i) => (
										<span key={i} className={`${PILL_TAG} text-sm`}>
											{pillar}
										</span>
									))}
									{pillarsTags.displayTags.length === 0 && (
										<span className="text-sm text-[var(--text-muted)]">
											No content pillars defined yet
										</span>
									)}
								</div>
							)}
						</EditableCard>
					</div>
				</AnimateIn>

				{/* Column 2: Writing Styles + Emojis */}
				<AnimateIn delay={1} className="h-full">
					<div className="flex h-full flex-col gap-6">
						<EditableCard
							title="Writing Styles"
							isEditing={stylesTags.isEditing}
							onEdit={stylesTags.onEdit}
							onSave={stylesTags.onSave}
							onCancel={stylesTags.onCancel}
						>
							{stylesTags.isEditing ? (
								<EditableTagList tags={stylesTags} placeholder="Add style" />
							) : (
								<div className="flex flex-wrap gap-2">
									{stylesTags.displayTags.map((tag, i) => (
										<span key={i} className={`${PILL_TAG} text-sm`}>
											{tag}
										</span>
									))}
									{stylesTags.displayTags.length === 0 && (
										<span className="text-sm text-[var(--text-muted)]">
											No styles defined yet
										</span>
									)}
								</div>
							)}
						</EditableCard>

						<EditableCard
							title="Emojis"
							isEditing={emojisTags.isEditing}
							onEdit={emojisTags.onEdit}
							onSave={emojisTags.onSave}
							onCancel={emojisTags.onCancel}
							className="flex-1"
						>
							{emojisTags.isEditing ? (
								<EditableTagList tags={emojisTags} placeholder="Add emoji" />
							) : (
								<div className="flex flex-wrap gap-3">
									{emojisTags.displayTags.map((emoji, i) => (
										<div
											key={i}
											className="flex h-12 w-12 items-center justify-center
												rounded-full border border-[var(--glass-border)]
												bg-[var(--glass-bg)] text-xl"
										>
											{emoji}
										</div>
									))}
									{emojisTags.displayTags.length === 0 && (
										<span className="text-sm text-[var(--text-muted)]">
											No emojis defined yet
										</span>
									)}
								</div>
							)}
						</EditableCard>
					</div>
				</AnimateIn>

				{/* Column 3: Output Examples */}
				<AnimateIn delay={2} className="h-full">
					<div className="flex h-full">
						<div className="border-l border-[var(--glass-border)]" />

						<div className="flex flex-1 flex-col pl-6">
							<div className="flex items-center justify-between">
								<Heading level={3} styles={cardTitleStyles}>
									Output Examples
								</Heading>
								<div className={isGenerating ? 'invisible' : ''}>
									<Button
										variant="accent"
										onPress={() => void handleTestVoice()}
									>
										Test voice
									</Button>
								</div>
							</div>

							<div
								className="mt-4 flex flex-1 flex-col border-t
									border-[var(--glass-border)] pt-4"
							>
								{isGenerating ? (
									<div className="flex flex-1 items-center justify-center">
										<div
											className="flex items-center gap-2 text-sm
												text-[var(--text-muted)]"
										>
											<ProgressCircle
												aria-label="Generating"
												isIndeterminate
												size="S"
											/>
											Generating sample...
										</div>
									</div>
								) : output ? (
									<>
										<div className="flex items-center gap-2 text-sm font-medium">
											<InstagramIcon />
											Instagram Posts
										</div>

										<div className="mt-3">
											<div className="text-xs text-[var(--text-muted)]">
												Caption
											</div>
											<p className="mt-1 text-sm whitespace-pre-wrap">
												{output}
											</p>
										</div>
									</>
								) : null}
							</div>
						</div>
					</div>
				</AnimateIn>
			</div>
		</>
	);
});

VoiceTab.displayName = 'VoiceTab';
